import type { Accessor, JSX, ParentProps, Setter } from "solid-js";
import { Show, createContext, createSignal, createUniqueId, splitProps, useContext } from "solid-js";

import { type VariantProps, cva } from "class-variance-authority";

import { createDisclosureState } from "@kobalte/core";
import createPresence from "solid-presence";
import { stopPropagation } from "~/util/helpers";
import { cn } from "~/util/utils";

interface SheetContext {
	// State for the sheet being open or closed
	open: Accessor<boolean>;
	close: () => void;

	// Unique IDs for the title and description
	titleId: string;
	descriptionId: string;

	// DOM references for the overlay and content
	overlayRef: Accessor<HTMLElement | undefined>;
	contentRef: Accessor<HTMLElement | undefined>;
	setOverlayRef: Setter<HTMLElement | undefined>;
	setContentRef: Setter<HTMLElement | undefined>;

	// Presence state for the overlay and content
	overlayPresent: Accessor<boolean>;
	contentPresent: Accessor<boolean>;
}

interface SheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const SheetContext = createContext<SheetContext>();
const useSheetContext = () => {
	const context = useContext(SheetContext);

	if (!context) {
		throw new Error("Cannot use `useSheetContext` outside of a `Sheet` component");
	}

	return context;
};

const Sheet = (props: ParentProps<SheetProps>) => {
	const [overlayRef, setOverlayRef] = createSignal<HTMLElement | null>(null);
	const [contentRef, setContentRef] = createSignal<HTMLElement | null>(null);

	const disclosure = createDisclosureState({
		open: () => props.open,
		onOpenChange: props.onOpenChange,
	});

	const { present: contentPresent } = createPresence({
		show: disclosure.isOpen,
		element: contentRef,
	});

	const { present: overlayPresent } = createPresence({
		show: disclosure.isOpen,
		element: overlayRef,
	});

	const providerValue = {
		open: disclosure.isOpen,
		close: disclosure.close,
		titleId: createUniqueId(),
		descriptionId: createUniqueId(),
		contentRef,
		overlayRef,
		setContentRef,
		setOverlayRef,
		contentPresent,
		overlayPresent,
	} as SheetContext;

	return <SheetContext.Provider value={providerValue}>{props.children}</SheetContext.Provider>;
};

const portalVariants = cva("fixed inset-0 z-[300] flex", {
	variants: {
		position: {
			top: "items-start",
			bottom: "items-end",
			left: "justify-start",
			right: "justify-end",
		},
	},
	defaultVariants: {
		position: "right",
	},
});

interface SheetPortalProps extends JSX.HTMLAttributes<HTMLDivElement> {
	position?: VariantProps<typeof portalVariants>["position"];
}

const SheetPortal = (props: SheetPortalProps) => {
	const [local, others] = splitProps(props, ["position", "children"]);
	const { close } = useSheetContext();

	return (
		<div
			class={portalVariants({ position: local.position })}
			onClick={close}
			{...others}
		>
			{local.children}
		</div>
	);
};

interface DialogOverlayProps extends JSX.HTMLAttributes<HTMLDivElement> {}

const SheetOverlay = (props: DialogOverlayProps) => {
	const [local, others] = splitProps(props, ["class"]);
	const { open, setOverlayRef, overlayPresent } = useSheetContext();

	return (
		<Show when={overlayPresent()}>
			<div
				ref={setOverlayRef}
				class={cn(
					"fixed inset-0 z-50 bg-black/80 backdrop-blur-sm",
					"data-[expanded]:animate-in data-[expanded]:fade-in-0",
					"data-[closed]:animate-out data-[closed]:fade-out-0",
					local.class,
				)}
				aria-hidden="true"
				data-expanded={open() ? "true" : undefined}
				data-closed={!open() ? "true" : undefined}
				{...others}
			/>
		</Show>
	);
};

const sheetVariants = cva(
	"fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[closed]:duration-300 data-[expanded]:duration-500 data-[expanded]:animate-in data-[closed]:animate-out",
	{
		variants: {
			position: {
				top: "inset-x-0 top-0 border-b data-[closed]:slide-out-to-top data-[expanded]:slide-in-from-top",
				bottom: "inset-x-0 bottom-0 border-t data-[closed]:slide-out-to-bottom data-[expanded]:slide-in-from-bottom",
				left: "inset-y-0 left-0 h-full w-3/4 border-r data-[closed]:slide-out-to-left data-[expanded]:slide-in-from-left sm:max-w-sm",
				right: "inset-y-0 right-0 h-full w-3/4 border-l data-[closed]:slide-out-to-right data-[expanded]:slide-in-from-right sm:max-w-sm",
			},
		},
		defaultVariants: {
			position: "right",
		},
	},
);

interface DialogContentProps extends JSX.HTMLAttributes<HTMLDivElement> {
	position?: VariantProps<typeof sheetVariants>["position"];
}

const SheetContent = (props: DialogContentProps) => {
	const [local, others] = splitProps(props, ["position", "class", "children"]);

	const { titleId, descriptionId, open, setContentRef, contentPresent } = useSheetContext();

	return (
		<Show when={contentPresent()}>
			<SheetPortal position={local.position}>
				<SheetOverlay />
				<div
					ref={setContentRef}
					class={cn(sheetVariants({ position: local.position }), local.class)}
					role="dialog"
					aria-labelledby={`yaa-sheet-title-${titleId}`}
					aria-describedby={`yaa-sheet-description-${descriptionId}`}
					data-expanded={open() ? "" : undefined}
					data-closed={!open() ? "" : undefined}
					onClick={stopPropagation}
					{...others}
				>
					{local.children}
				</div>
			</SheetPortal>
		</Show>
	);
};

const SheetHeader = (props: JSX.HTMLAttributes<HTMLDivElement>) => {
	const [local, others] = splitProps(props, ["class"]);
	return (
		<div
			class={cn("flex flex-col space-y-2 text-center sm:text-left", local.class)}
			{...others}
		/>
	);
};

const SheetFooter = (props: JSX.HTMLAttributes<HTMLDivElement>) => {
	const [local, others] = splitProps(props, ["class"]);
	return (
		<div
			class={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", local.class)}
			{...others}
		/>
	);
};

interface DialogTitleProps extends JSX.HTMLAttributes<HTMLHeadingElement> {}

const SheetTitle = (props: DialogTitleProps) => {
	const [local, others] = splitProps(props, ["class"]);
	return (
		<h2
			class={cn("text-lg font-semibold text-foreground", local.class)}
			{...others}
		/>
	);
};

interface DialogDescriptionProps extends JSX.HTMLAttributes<HTMLParagraphElement> {}

const SheetDescription = (props: DialogDescriptionProps) => {
	const [local, others] = splitProps(props, ["class"]);
	return (
		<p
			class={cn("text-sm text-muted-foreground", local.class)}
			{...others}
		/>
	);
};

export { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription };
